import type { TeamCommands } from "../../hooks/teamTypes";
import type { PlayerListItem } from "../../shared/types/contracts";
import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { SectionCard } from "../../shared/ui/section-card";
import { LineupEditor } from "./LineupEditor";
import type { LineupMode } from "./setupTypes";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";

interface LineupSetupStepProps {
  players: PlayerListItem[];
  activePlayerIds: string[];
  lineupSlots: Array<string | null>;
  lineupMode: LineupMode;
  battingCount: number;
  onSuggestLineup: () => void;
  onCommitLineup: TeamCommands["reorderLineup"];
  onSetAttendance: TeamCommands["setAttendance"];
  actionStatus: IntentStatusView;
}

export function LineupSetupStep({
  players,
  activePlayerIds,
  lineupSlots,
  lineupMode,
  battingCount,
  onSuggestLineup,
  onCommitLineup,
  onSetAttendance,
  actionStatus,
}: LineupSetupStepProps) {
  if (activePlayerIds.length === 0 && players.length === 0) return null;
  const suggestPending = actionStatus.isPending(actionKeys.suggestLineup);
  const reorderPending = actionStatus.isPending(actionKeys.reorderLineup);
  const suggestUnknown =
    actionStatus.phaseFor(actionKeys.suggestLineup) === "outcome_unknown";
  const reorderUnknown =
    actionStatus.phaseFor(actionKeys.reorderLineup) === "outcome_unknown";

  return (
    <SectionCard
      title="2 · BATTING ORDER"
      description={
        lineupMode === "live"
          ? "Reorder the current lineup. Players cannot be added or benched after first pitch."
          : lineupMode === "locked"
            ? "The completed game lineup is locked."
            : "Drag players between the lineup and bench. An OUT player dragged into the lineup is marked IN first."
      }
      meta={<Badge variant="accent">{battingCount} BATTERS</Badge>}
      actions={
        <Button
          type="button"
          onClick={onSuggestLineup}
          disabled={lineupMode === "locked" || battingCount === 0}
          size="xs"
          variant="outline"
          className="hidden border-warning text-warning min-[440px]:inline-flex"
          pending={suggestPending}
          pendingLabel={suggestUnknown ? "CHECKING…" : "SUGGESTING…"}
        >
          ✨ SUGGEST FROM SEASON STATS
        </Button>
      }
      contentClassName="px-2 pb-2"
    >
      <Button
        type="button"
        onClick={onSuggestLineup}
        disabled={lineupMode === "locked" || battingCount === 0}
        size="xs"
        variant="outline"
        className="mb-2 w-full border-warning text-warning min-[440px]:hidden"
        pending={suggestPending}
        pendingLabel={suggestUnknown ? "CHECKING…" : "SUGGESTING…"}
      >
        ✨ SUGGEST FROM SEASON STATS
      </Button>
      <div
        aria-live="polite"
        className="min-h-4 px-1 font-mono text-[0.62rem] font-bold tracking-[0.08em] text-foreground-muted"
      >
        {reorderPending
          ? reorderUnknown
            ? "CHECKING ORDER…"
            : "SAVING ORDER…"
          : ""}
      </div>
      <LineupEditor
        players={players}
        activeIds={activePlayerIds}
        lineupSlots={lineupSlots}
        onCommit={onCommitLineup}
        onSetAttendance={onSetAttendance}
        saving={reorderPending}
        mode={lineupMode}
      />
    </SectionCard>
  );
}
